import { z } from 'zod';
import { STOCK_REQUEST_STATUSES } from '../constants/stock-request-states.js';
import { uuidSchema } from './common.js';
export const stockRequestItemInputSchema = z.object({
    productId: uuidSchema,
    qty: z.number().int().positive().max(999),
    note: z.string().trim().max(500).nullable().default(null),
});
export const createStockRequestSchema = z
    .object({
    /** The location being asked to send stock. */
    sourceLocationId: uuidSchema,
    /** The location asking for it — normally wherever the caller is signed in. */
    destinationLocationId: uuidSchema,
    note: z.string().trim().max(1000).nullable().default(null),
    items: z.array(stockRequestItemInputSchema).min(1).max(50),
})
    .refine((v) => v.sourceLocationId !== v.destinationLocationId, {
    message: 'Choose two different locations',
    path: ['sourceLocationId'],
});
export const transitionStockRequestSchema = z.object({
    to: z.enum(STOCK_REQUEST_STATUSES),
    note: z.string().trim().max(500).nullable().default(null),
});
/**
 * Only while a request is still `requested` — the moment the other side
 * accepts, they have committed to sending exactly what was asked; changing
 * the ask after that goes through cancel-and-reraise instead, same as an
 * order does.
 */
export const updateStockRequestSchema = z
    .object({
    note: z.string().trim().max(1000).nullable().optional(),
    items: z.array(stockRequestItemInputSchema).min(1).max(50).optional(),
})
    .refine((v) => v.note !== undefined || v.items !== undefined, { message: 'Nothing to update' });
export const stockRequestItemSchema = z.object({
    id: uuidSchema,
    productId: uuidSchema,
    productName: z.string(),
    sku: z.string(),
    qty: z.number().int(),
    /** Null until the request is fulfilled; may be less than `qty` if the source came up short. */
    fulfilledQty: z.number().int().nullable(),
    note: z.string().nullable(),
});
export const stockRequestShortfallSchema = z.object({
    productName: z.string(),
    sku: z.string(),
    requestedQty: z.number().int(),
    fulfilledQty: z.number().int(),
});
/**
 * A read-only preview of what fulfilling would actually move, checked before
 * the fact so staff can decide rather than discover a shortfall after the
 * stock has already moved. Not locked, so it is advisory — the transition
 * itself re-checks under a row lock at the moment it actually moves anything.
 */
export const stockRequestAvailabilitySchema = z.object({
    productId: uuidSchema,
    productName: z.string(),
    sku: z.string(),
    requestedQty: z.number().int(),
    availableQty: z.number().int(),
});
export const stockRequestSchema = z.object({
    id: uuidSchema,
    requestNumber: z.string(),
    status: z.enum(STOCK_REQUEST_STATUSES),
    sourceLocationId: uuidSchema,
    sourceLocationName: z.string(),
    destinationLocationId: uuidSchema,
    destinationLocationName: z.string(),
    note: z.string().nullable(),
    /**
     * Set when a sale took this stock off the source location's shelf. Such a
     * request is an errand — hand the goods over — and moves no stock when it is
     * fulfilled, because the order already accounted for it.
     */
    orderId: uuidSchema.nullable(),
    orderNumber: z.string().nullable(),
    createdByLabel: z.string().nullable(),
    items: z.array(stockRequestItemSchema),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const stockRequestListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    status: z.enum(STOCK_REQUEST_STATUSES).optional(),
    locationId: uuidSchema.optional(),
    /**
     * Relative to `locationId`: `incoming` = this location is being asked to
     * send stock (it is the source); `outgoing` = this location did the asking.
     * Meaningless without `locationId`, so ignored unless one is given.
     */
    direction: z.enum(['incoming', 'outgoing']).optional(),
    sort: z.enum(['createdAt']).default('createdAt'),
    order: z.enum(['asc', 'desc']).default('desc'),
});
export const stockRequestEventSchema = z.object({
    id: uuidSchema,
    fromStatus: z.enum(STOCK_REQUEST_STATUSES).nullable(),
    toStatus: z.enum(STOCK_REQUEST_STATUSES),
    actorType: z.enum(['staff', 'customer', 'kiosk', 'system']),
    actorLabel: z.string().nullable(),
    note: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
//# sourceMappingURL=stock-request.js.map